import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, FileCheck2, LayoutGrid, MessageSquare, Scale, ShieldCheck, Sparkles, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DitherCanvas } from "@/components/shader/DitherCanvas";

const steps = [
  "Find a creator. Find the thing they sell.",
  "Fill out their brief. Real questions, real answers.",
  "See the price, or ask for a quote.",
  "Pay the deposit. You're in the queue.",
  "Watch the job move through real stages.",
  "Approve a checkpoint, or send it back.",
  "Pay the balance. Files unlock.",
  "Leave a note. Rebook in one click.",
];

const pillars = [
  { icon: LayoutGrid, title: "A page, not a vibe.", body: "Your page sells something specific. Price, turnaround, revisions, a button. No linktree and a prayer." },
  { icon: FileCheck2, title: "Briefs, not guessing.", body: "You write the questions. Clients have to answer them. Nobody DMs you at 3am asking what you need." },
  { icon: Scale, title: "Quote or checkout.", body: "Easy stuff checks out instantly. Harder stuff gets a real quote with a real ETA. Never 'DM for pricing'." },
  { icon: MessageSquare, title: "One thread, one job.", body: "Messages, WIPs, approvals, delivery — all on the job. No scavenger hunt across five apps." },
  { icon: ShieldCheck, title: "Rules, not arguments.", body: "Included rounds, minor vs. major, overages. Written down before the work starts. The fourth revision stops being a fight." },
  { icon: Wallet, title: "Money first, then files.", body: "Deposit books the slot. Final unlocks delivery. If a client ghosts, the money already cleared." },
];

export function HomePage() {
  return (
    <div>
      <section className="relative min-h-[92vh] overflow-hidden bg-black text-white">
        <DitherCanvas className="absolute inset-0 h-full w-full" pixelSize={3} />
        <div className="pointer-events-none absolute inset-0 bg-black/65" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/25" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-black" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(70% 60% at 30% 45%, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0) 80%)",
          }}
        />

        <div className="relative z-10 mx-auto flex min-h-[92vh] max-w-6xl flex-col justify-between px-4 pt-32 pb-20 [text-shadow:0_1px_24px_rgba(0,0,0,0.65)]">
          <div className="max-w-3xl">
            <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/40 px-3 py-1 text-xs font-medium uppercase tracking-[0.25em] backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" /> For people who make things for money
            </p>
            <h1 className="text-balance font-semibold leading-[1.02] tracking-tight text-white">
              <span className="block text-5xl md:text-7xl">Stop running a shop</span>
              <span className="block text-5xl md:text-7xl">out of your DMs.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-white md:text-lg">
              Put the work on a real page. Price it. Let clients book with a real brief. Take the
              deposit before you open the tablet. Ship, invoice, done.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="gap-2">
                <Link to="/signup">
                  Put a shop up <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="border border-white/20 bg-white/10 text-white hover:bg-white/20"
              >
                <Link to="/discover">Find a creator</Link>
              </Button>
            </div>
          </div>

          <div className="mt-24 grid max-w-2xl grid-cols-2 gap-x-6 gap-y-3 text-sm text-white md:grid-cols-2">
            {[
              "Clients see the stage, so they stop asking.",
              "Creators stop being unpaid therapists.",
              "Scope, revisions, payment — one place.",
              "Delivered files live in a vault, not a Discord DM.",
            ].map(p => (
              <div key={p} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                <span>{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b bg-background">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <div className="mb-12 max-w-2xl">
            <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
              This is a tool. Not a feed. Not a community.
            </h2>
            <p className="mt-4 text-muted-foreground">
              No algorithm. No dopamine drip. You're here to make something someone paid for. We
              handle the boring parts so you can handle the craft.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pillars.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-xl border bg-card p-5 transition hover:border-foreground/20"
              >
                <Icon className="mb-3 h-5 w-5 text-muted-foreground" />
                <h3 className="mb-1 font-medium">{title}</h3>
                <p className="text-sm text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b bg-background">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <div className="mb-10 max-w-2xl">
            <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
              Eight steps. No surprises.
            </h2>
            <p className="mt-3 text-muted-foreground">
              Every step is a real state the job moves through. Both sides can see it. Both sides
              know what's next.
            </p>
          </div>
          <ol className="grid gap-3 md:grid-cols-2">
            {steps.map((s, i) => (
              <li
                key={s}
                className="flex items-start gap-4 rounded-lg border bg-card p-4 text-sm"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background text-xs font-semibold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-6xl px-4 py-24 text-center">
          <h2 className="text-4xl font-semibold tracking-tight md:text-5xl">
            Put the shop up.
            <br />
            Price the work. Get paid.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg">
              <Link to="/signup">Create your fanfix</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/discover">Find a creator</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
